import { permissionsQueryOptions } from "@/api/permissions/permissions.api";
import {
  requiredModuleByPath,
  type AccessResult,
  type ModuleKey,
  type PermissionsResponse,
} from "@/api/permissions/permissions.types";
import { SERVICE_PROVIDER, SUPER_ADMIN } from "@/components/data/constants";
import type { Section } from "@/components/features/AuthenticatedLayout/Sidebar/Sidebar";
import { ENTITY_ACCESS_ROUTE_KEYS } from "@/constants/routesConfig";
import { useMyEntities } from "@/hooks/entities/useEntitiesQueries";
import { useActiveSession } from "@/hooks/useActiveSession";
import { useRequestScope } from "@/hooks/useRequestScope";
import { isEntityScoped, useEntityTenantStore } from "@/store/entityTenantStore";
import { isAppLevelUser } from "@/utils/isPlatformView";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useCallback } from "react";

const ALL_TRUE: AccessResult = { view: true, create: true, edit: true };
const ALL_FALSE: AccessResult = { view: false, create: false, edit: false };

type CheckArgs =
  | {
      module: ModuleKey;
      path?: never;
    }
  | {
      path: string;
      module?: never;
    };

// SERV-3232: an app-level session's module list is the union across her
// entities. Once she picks one, the sidebar follows the role she holds there.
function useScopedModules(sessionModules: string[], appLevelSession: boolean): string[] {
  const entityTenant = useEntityTenantStore((state) => state.entityTenant);
  const { data: myEntities } = useMyEntities(appLevelSession);

  if (!appLevelSession || !isEntityScoped(entityTenant)) return sessionModules;
  // After a reload the picked entity comes back from storage before the entity
  // list does; an empty list here emptied the sidebar.
  if (myEntities === undefined) return sessionModules;
  return myEntities.find((entity) => entity.id === entityTenant)?.modules ?? [];
}

export function usePermissions() {
  const {
    userType,
    modules: sessionModules,
    isEntityLogin,
    isReactiveHire,
    isAppLevel,
    allEntities,
  } = useActiveSession();

  const isSuperAdmin = userType === SUPER_ADMIN;
  const isServiceProvider = userType === SERVICE_PROVIDER;
  const appLevelSession =
    isAppLevelUser({ isAppLevel, allEntities: allEntities ?? false, userType }) && !isEntityLogin;
  const moduleArr = useScopedModules(sessionModules ?? [], appLevelSession);

  const requestScope = useRequestScope();
  const scopedPermissionsQueryOptions = permissionsQueryOptions(requestScope);
  const { data: permissions } = useSuspenseQuery<PermissionsResponse>(
    scopedPermissionsQueryOptions,
  );

  // single module → map to { view, create, edit }
  const getForModule = useCallback(
    (moduleKey: ModuleKey | null): AccessResult => {
      if (!moduleKey) return ALL_FALSE;

      // get module permissions
      const modulePermissions = permissions?.data?.[moduleKey]?.permissions;

      // if module permissions not found, return false
      if (!modulePermissions) return ALL_FALSE;

      return {
        view: !!modulePermissions.view,
        create: !!modulePermissions.create,
        edit: !!modulePermissions.edit,
      };
    },
    [permissions],
  );

  const hasAccess = useCallback(
    (args: CheckArgs): AccessResult => {
      // super admin: full access
      if (isSuperAdmin) return ALL_TRUE;

      // check by module (using MODULE_KEYS_MAP values)
      if ("module" in args && args.module) {
        return getForModule(args.module);
      }

      // check by path (using requiredModuleByPath)
      if ("path" in args && args.path) {
        const requiredModule = requiredModuleByPath[args.path];

        // treat paths not tied to a module as "public"
        if (requiredModule == null) return ALL_TRUE; // public route

        return getForModule(requiredModule);
      }

      return ALL_FALSE;
    },
    [getForModule, isSuperAdmin],
  );

  // Helper: filter your sidebar structure - not connected to permissions api, based on modules array from the id-token payload
  const filterSidebar = useCallback(
    (sections: Section[]) => {
      if (isSuperAdmin) {
        if (isEntityLogin) {
          return sections
            .map((section) => ({
              ...section,
              items: section.items.filter(
                (item) => item.to === "/" || ENTITY_ACCESS_ROUTE_KEYS.has(item.to),
              ),
            }))
            .filter((section) => section.items.length > 0);
        }
        return sections;
      }

      // if modules not loaded yet, return empty array
      if (!moduleArr || moduleArr.length === 0) return [];

      const allowed = new Set(moduleArr);

      return sections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => !item.module || allowed.has(item.module)),
        }))
        .filter((section) => section.items.length > 0);
    },
    [isSuperAdmin, isEntityLogin, moduleArr],
  );

  return {
    hasAccess,
    isSuperAdmin,
    filterSidebar,
    permissionsQueryOptions: scopedPermissionsQueryOptions,
    isServiceProvider,
    isReactiveHire,
  };
}

export function getModuleAccess(
  permissions: PermissionsResponse | undefined,
  moduleKey: ModuleKey | null,
): AccessResult {
  if (!moduleKey || !permissions) return ALL_FALSE;

  const hasPermissions = permissions?.data?.[moduleKey]?.permissions;
  if (!hasPermissions) return ALL_FALSE;

  return {
    view: !!hasPermissions.view,
    create: !!hasPermissions.create,
    edit: !!hasPermissions.edit,
  };
}
